import { Api } from "../../Api";
import { DataFrame, Method, Status } from "../../DataFrame";
import { DependencyContainer } from "../../DependencyContainer";
import { Guid } from "../../Guid";
import { Helpers } from "../../Helpers";
import { ControllerConfigurationService } from "../Controller/ControllerConfigurationService";
import { GeolocationService } from "../Controller/GeolocationService";
import { IControllerConfigurationService } from "../Controller/IControllerConfigurationService";
import { Coordinates, CoordinatesWithSourceInfo, IGeolocationService } from "../Controller/IGeolocationService";
import { DevicesService } from "../Devices/DevicesService";
import { DeviceResponseType, DeviceTaskType, IDevice } from "../Devices/IDevice";
import { BatteryState, BinarySensorState, BlindPostionState, BlindRemoteButtonState, ChannelOnOffState, CurrentWindThresholdState, DeviceState, DoorBellState, EnergyMeasurementParameter, FacadeRemoteButtonState, FloodSensorState, GatePositionState, HumiditySensorState, IDeviceStateData, LightBrightnessState, LightColorState, LightRGBWState, LightWarmthState, MeasuredBrightnessState, MeasuredEnergyState, MeasuredTemperatureState, MovementSensorState, PressureSensorState, ReedState, RemoteButtonState, StateReliability, WindSpeedState } from "../Devices/IDeviceState";
import { DeviceControlFeature, IDeviceTask } from "../Devices/IDeviceTask";
import { IDevicesService } from "../Devices/IDevicesService";
import { DeviceTaskExecutionResult } from "../Devices/TaskExecutionResult";
import { DeviceTask } from "../Devices/Tasks/DeviceTask";
import { BlindActionEnum, ChangeGatePositionPulse, ChangeGatewayPositionPulse, FacadeActionEnum, GetDeviceState, GetFacadeType, IdentifyDevice, LightRGBW, SetBlindMicroventilation, SetBlindOpenCloseTime, SetBlindPosition, SetBlindPositionSimple, SetFacadePosition, SetGatePositionPrecise, SetLightBrightness, SetLightBrightnessDynamicly, SetLightColor, SetLightTemperature, SetTemperature, ToggleState, TurnOff, TurnOn, TurnOnWithTime } from "../Devices/Tasks/Tasks";
import { ExalusConnectionService } from "../ExalusConnectionService";
import { ResponseResult } from "../FieldChangeResult";
import { IExalusConnectionService } from "../IExalusConnectionService";
import { ILoggerService } from "../Logging/ILoggerService";
import { LoggerService } from "../Logging/LoggerService";
import { IUpdatesProvider } from "../Updates/IUpdatesProvider";
import { UpdatesProvider } from "../Updates/UpdatesProvider";
import { IScenesService, ScenesServiceErrorCode } from "./IScenesService";
import { DeviceStateArgument, DeviceStateComparisonMethod, DeviceStateComparisonParams, NumberParam, TimeSpanParam } from "./LeftArgumentTypes";
import { ArgumentType, ConditionsTypes, HandledType, ISequence, ISequenceInfo, ILeftArgument, LeftArgumentTypes, UnsupportedScenesDeviceState, ISequenceTasks, DeviceStateType, IRawSequence, ScenesUtils, ISceneTask, SceneTaskTypeNum, SceneTasks, SceneTaskType, ScenesUsedIn, SupportedTaskTypes } from "./Scenes";
import { SequenceBuilder, SequenceRequestData } from "./ScenesBuilder";

/**
 * @type Class
 */
export class ScenesService implements IScenesService {
    public static readonly ServiceName: string = "ScenesService";
    private _connection: IExalusConnectionService;
    private _configurationService: IControllerConfigurationService;

    constructor() {
        this._connection = Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName);
        this._configurationService = Api.Get<IControllerConfigurationService>(ControllerConfigurationService.ServiceName);
    }

    GetServiceName(): string {
        return ScenesService.ServiceName;
    }

    async GetSequencesListAsync(): Promise<ResponseResult<ScenesServiceErrorCode> | ISequenceInfo[]> {
        const result = await this._connection?.SendAndWaitForResponseAsync<ISequenceInfo[]>(new GetSequencesListRequest(), 12000, false);

        DependencyContainer.Log?.Debug(`GetSequencesListAsync result:\n ${JSON.stringify(result)}}`);

        if (result == null)
            return new ResponseResult(ScenesServiceErrorCode.OtherError, `Cannot get sequences list - response is null.`);

        if (result.Status != Status.OK)
            return new ResponseResult(ScenesServiceErrorCode.OtherError, `Cannot get sequences list - controller responded with response code ${result.Status}.`);

        if (result.Data == null)
            return new ResponseResult(ScenesServiceErrorCode.NoData, `Cannot get sequences list - controller responded with status OK, but response does not contain data!`);

        return result.Data;
    }

    GetSequenceAsync(sequenceInfo: ISequenceInfo): Promise<ResponseResult<ScenesServiceErrorCode> | (ISequence & IRawSequence)>;
    GetSequenceAsync(sequenceGuid: string): Promise<ResponseResult<ScenesServiceErrorCode> | (ISequence & IRawSequence)>;
    async GetSequenceAsync(sequence: unknown): Promise<ResponseResult<ScenesServiceErrorCode> | (ISequence & IRawSequence)> {
        let sequenceGuid: string = "";

        if (typeof sequence == 'string') {
            sequenceGuid = sequence;
        }
        else {
            sequenceGuid = (sequence as ISequenceInfo).Guid;
        }

        const result = await this._connection?.SendAndWaitForResponseAsync<any>(new GetSequenceDetailsRequest(sequenceGuid), 12000, false);

        if (result == null)
            return new ResponseResult(ScenesServiceErrorCode.OtherError, `Cannot get sequence - response is null.`);

        if (result.Status != Status.OK)
            return new ResponseResult(ScenesServiceErrorCode.OtherError, `Cannot get sequence - controller responded with response code ${result.Status}.`);

        if (result.Data == null)
            return new ResponseResult(ScenesServiceErrorCode.NoData, `Cannot get sequence - controller responded with status OK, but response does not contain data!`);

        const raw = result.Data;
        let tmp = this.MapSequence(raw);


        if (typeof sequence == 'string') {
            let sl = await this.GetSequencesListAsync();

            if (sl instanceof ResponseResult) {
                let dat = sl as ResponseResult<ScenesServiceErrorCode>;
                console.warn(`Cannot get sequence - response is null. Error: ${dat.Data} ${dat.Type}`);
            }
            else {
                const seq = sl.find(s => s.Guid == sequenceGuid);

                if (!seq)
                    return new ResponseResult(ScenesServiceErrorCode.NoData, `Cannot get sequence - controller responded with status OK, but response does not contain data!`);
                else {
                    tmp.IsDisabled = seq.IsDisabled;
                }
            }
        }
        else {
            const sequenceInfo = raw as ISequenceInfo;
            tmp.IsDisabled = sequenceInfo.IsDisabled;
        }

        return tmp;
    }

    GetSequenceBuilder(newSequenceName: string, newSequenceIcon: string): SequenceBuilder
    GetSequenceBuilder(newSequenceName: string): SequenceBuilder
    GetSequenceBuilder(): SequenceBuilder
    GetSequenceBuilder(newSequenceName?: string, newSequenceIcon?: string) {
        return new SequenceBuilder(newSequenceName, newSequenceIcon);
    }

    async CreateSequenceAsync(sequence: SequenceRequestData): Promise<Status> {
        try {
            await this._configurationService.EnterConfigurationModeAsync();
            DependencyContainer.Log?.Debug(`Creating sequence ${sequence.Name} json: \n${JSON.stringify(sequence)}`);
            const result = await this._connection?.SendAndWaitForResponseAsync<any>(new CreateSequenceRequest(sequence), 20000, false);

            if (result == null || result.Status == null)
                return Status.Error;

            return result.Status;
        }
        catch (error) {
            DependencyContainer.Log?.Error(`Creating sequence ${sequence.Name} error: ${error}`);
            return Status.FatalError;
        }
        finally {
            DependencyContainer.Log?.Error(ScenesService.ServiceName, `EXIT CONFIGURATION MODE WILL BE FIRED! CreateSequenceAsync()`);
            await this._configurationService.ExitConfigurationModeAsync();
            await Api.Get<IDevicesService>(DevicesService.ServiceName).GetDevicesAsync(true);
        }
    }

    CheckIfSequenceIsUsedInAnotherSequenceAsync(sequenceInfo: ISequenceInfo): Promise<ScenesUsedIn | ResponseResult<ScenesServiceErrorCode>>;
    CheckIfSequenceIsUsedInAnotherSequenceAsync(sequenceGuid: string): Promise<ScenesUsedIn | ResponseResult<ScenesServiceErrorCode>>;
    async CheckIfSequenceIsUsedInAnotherSequenceAsync(sequence: unknown): Promise<ScenesUsedIn | ResponseResult<ScenesServiceErrorCode>> {
        try {
            let sequenceGuid: string = "";

            if (typeof sequence == 'string') {
                sequenceGuid = sequence;
            }
            else {
                sequenceGuid = (sequence as ISequenceInfo).Guid;
            }

            const result = await this._connection?.SendAndWaitForResponseAsync<ScenesUsedIn>(new GetSequenceUsedInRequest(sequenceGuid), 12000, false);

            if (result == null)
                return new ResponseResult(ScenesServiceErrorCode.OtherError, `Cannot get sequence - response is null.`);

            switch (result.Status) {
                case Status.WrongData:
                    return new ResponseResult(ScenesServiceErrorCode.InvalidSceneGuid, `Cannot check if sequence is used in another sequence! Sequence not found, invalid sequence guid!`);
                case Status.OK:
                    if (result.Data == null)
                        return new ResponseResult(ScenesServiceErrorCode.NoData, `Cannot get sequence - controller responded with status OK, but response does not contain data!`);
                    else
                        return result.Data;
                default:
                    return new ResponseResult(ScenesServiceErrorCode.OtherError, `Cannot check if sequence is used in another sequence! Controller responded with code: ${result.Status}`);
            }

        } catch (error) {
            return new ResponseResult(ScenesServiceErrorCode.FatalError, `Cannot check if sequence is used in another sequence! Fatal error! ${error}`);
        }
    }

    DeleteSequenceAsync(sequenceInfo: ISequenceInfo): Promise<Status>;
    DeleteSequenceAsync(sequenceGuid: string): Promise<Status>;
    async DeleteSequenceAsync(sequence: unknown): Promise<Status> {
        try {
            let sequenceGuid: string = "";

            if (typeof sequence == 'string') {
                sequenceGuid = sequence;
            }
            else {
                sequenceGuid = (sequence as ISequenceInfo).Guid;
            }

            await this._configurationService.EnterConfigurationModeAsync();
            const result = await this._connection?.SendAndWaitForResponseAsync<any>(new DeleteSequenceRequest(sequenceGuid), 12000, false);

            if (result == null || result.Status == null)
                return Status.Error;

            return result.Status;
        }
        catch (error) {
            return Status.FatalError;
        }
        finally {
            await this._configurationService.ExitConfigurationModeAsync();
            await Api.Get<IDevicesService>(DevicesService.ServiceName).GetDevicesAsync(true);
        }
    }

    async EditSequenceAsync(sequence: SequenceRequestData): Promise<Status> {
        try {
            const request: EditSequenceRequestFrame = {
                Guid: sequence.Guid,
                Icon: sequence.Icon,
                Name: sequence.Name,
                Condition: sequence
            }

            // fix for a bug
            var tmp = request.Condition.IsDisabled;
            request.Condition.IsDisabled = false;
            // /
            await this._configurationService.EnterConfigurationModeAsync();

            const result = await this._connection?.SendAndWaitForResponseAsync<any>(new EditSequenceRequest(request), 20000, false);
            // fix for a bug
            request.Condition.IsDisabled = tmp;
            // /
            if (result == null || result.Status == null)
                return Status.Error;

            return result.Status;
        }
        catch (error) {
            DependencyContainer.Log?.Error(`Editing sequence ${sequence.Name} error: ${error}`);
            return Status.FatalError;
        }
        finally {
            await this._configurationService.ExitConfigurationModeAsync();
            await Api.Get<IDevicesService>(DevicesService.ServiceName).GetDevicesAsync(true);
        }
    }

    EnableSequenceAsync(sequenceInfo: ISequenceInfo): Promise<Status>;
    EnableSequenceAsync(sequenceGuid: string): Promise<Status>;
    async EnableSequenceAsync(sequence: unknown): Promise<Status> {
        try {
            let sequenceGuid: string = "";

            if (typeof sequence == 'string') {
                sequenceGuid = sequence;
            }
            else {
                sequenceGuid = (sequence as ISequenceInfo).Guid;
            }

            const result = await this._connection?.SendAndWaitForResponseAsync<any>(new EnableSequenceRequest(sequenceGuid), 12000, false);

            if (result == null || result.Status == null)
                return Status.Error;

            return result.Status;
        }
        catch (error) {
            return Status.FatalError;
        }
        finally {
            await this._configurationService.ExitConfigurationModeAsync();
        }
    }

    DisableSequenceAsync(sequenceInfo: ISequenceInfo): Promise<Status>;
    DisableSequenceAsync(sequenceGuid: string): Promise<Status>;
    async DisableSequenceAsync(sequence: unknown): Promise<Status> {
        try {
            let sequenceGuid: string = "";

            if (typeof sequence == 'string') {
                sequenceGuid = sequence;
            }
            else {
                sequenceGuid = (sequence as ISequenceInfo).Guid;
            }

            const result = await this._connection?.SendAndWaitForResponseAsync<any>(new DisableSequenceRequest(sequenceGuid), 12000, false);

            if (result == null || result.Status == null)
                return Status.Error;

            return result.Status;
        }
        catch (error) {
            return Status.FatalError;
        }
        finally {
            await this._configurationService.ExitConfigurationModeAsync();
        }
    }

    RunSequenceAsync(sequenceInfo: ISequenceInfo): Promise<Status>;
    RunSequenceAsync(sequenceGuid: string): Promise<Status>;
    async RunSequenceAsync(sequence: unknown): Promise<Status> {
        let sequenceDeviceGuid: string = "";

        if (typeof sequence == 'string') {
            const res = await this.GetSequencesListAsync();

            if ((res as ResponseResult<ScenesServiceErrorCode>).Type != null)
                return Status.Error;
            else {
                const seq = (res as ISequenceInfo[]).find(s => s.Guid == sequence);

                if (!seq)
                    return Status.Error;

                sequenceDeviceGuid = seq.DeviceGuid;
            }

        }
        else {
            sequenceDeviceGuid = (sequence as ISequenceInfo).DeviceGuid;
        }
        const tsk = new TurnOn();
        tsk.Channel = 1;
        tsk.DeviceGuid = sequenceDeviceGuid;

        const result = await Api.Get<IDevicesService>(DevicesService.ServiceName).ExecuteDeviceTaskAsync({ Guid: sequenceDeviceGuid } as IDevice, tsk);

        switch (result) {
            case DeviceTaskExecutionResult.Executed:
                return Status.OK;
            case DeviceTaskExecutionResult.DeviceNotFound:
                return Status.WrongData;
            default:
                return Status.Error;
        }
    }

    async GetUtilsAsync(): Promise<ScenesUtils> {
        const astronomicalClockSunCalc = await this.SunCalculatorAsync();

        return {
            GetSunRiseTime: astronomicalClockSunCalc.GetSunRiseTime,
            GetSunSetTime: astronomicalClockSunCalc.GetSunSetTime,
            ParseLinearScaleToLux: this._parseLinearScaleToLux,
            ParseLuxToLinearScale: this._parseLuxToLinearScale,
        }
    }

    protected _parseLinearScaleToLux(linearScaleVal: number): number | undefined {
        if (linearScaleVal < 0.0 || linearScaleVal > 1.0)
            return undefined;

        return Math.round(Math.pow(10, (linearScaleVal * 5)));
    }

    protected _parseLuxToLinearScale(lux: number): number {
        if (lux <= 0)
            return 0

        const res = (Math.log10(lux) / 5.0);

        return res > 1.0 ? 1 : res;
    }

    protected async SunCalculatorAsync(): Promise<{ GetSunRiseTime: () => Date, GetSunSetTime: () => Date }> {
        const srv = Api.Get<IGeolocationService>(GeolocationService.ServiceName);
        const cords = new Coordinates();
        let recalculationDone = false;
        cords.Latitude = 49.9958888026741;
        cords.Longitude = 18.9172596008914;

        try {
            if ((await srv.IsControllerGeolocationSetAsync()) === true) {
                const controlerCords = await srv.GetControlllerGeolocationAsync();
                if (controlerCords instanceof CoordinatesWithSourceInfo) {
                    cords.Latitude = controlerCords.Latitude
                    cords.Longitude = controlerCords.Longitude
                }
            }
        } catch (error) {
            DependencyContainer.Log?.Warning(`Cannot get localization data, using default values: 49.9958888026741, 18.9172596008914. Error: ${error}`);
        }

        const hasDST = (date = new Date()) => {
            const january = new Date(
                date.getFullYear(),
                0,
                1,
            ).getTimezoneOffset();
            const july = new Date(
                date.getFullYear(),
                6,
                1,
            ).getTimezoneOffset();

            return Math.max(january, july) !== date.getTimezoneOffset();
        }

        const calculateDifferenceSunAndLocalTime = (dayNumberOfDateTime: number) => {
            const ellipticalOrbitPart1 = 7.95204 * Math.sin((0.01768 * dayNumberOfDateTime) + 3.03217);
            const ellipticalOrbitPart2 = 9.98906 * Math.sin((0.03383 * dayNumberOfDateTime) + 3.46870);

            let differenceSunAndLocalTime = ellipticalOrbitPart1 + ellipticalOrbitPart2 + (cords.Longitude - Math.round(cords.Longitude / 15) * 15) * 4;

            if (hasDST())
                differenceSunAndLocalTime -= 60;

            return differenceSunAndLocalTime;
        }

        const calculateDeclination = (numberOfDaysSinceFirstOfJanuary: number) => {
            return Math.asin(-0.39795 * Math.cos(2.0 * Math.PI * (numberOfDaysSinceFirstOfJanuary + 10.0) / 365.0));
        }

        const calculateTanSunPosition = (declanationOfTheSun: number) => {
            const limitTanSunPosition = (tanSunPosition: number) => {
                if (tanSunPosition < -1)
                    tanSunPosition = -1.0;

                if (tanSunPosition > 1)
                    tanSunPosition = 1.0;

                return tanSunPosition;
            }

            const latitudeRadians = cords.Latitude * Math.PI / 180;

            const sinSunPosition = Math.sin(latitudeRadians) * Math.sin(declanationOfTheSun);
            const cosSunPosition = Math.cos(latitudeRadians) * Math.cos(declanationOfTheSun);
            let tanSunPosition = sinSunPosition / cosSunPosition;
            tanSunPosition = limitTanSunPosition(tanSunPosition);
            return tanSunPosition;
        }

        const calculateSunRiseInternal = (tanSunPosition: number, differenceSunAndLocalTime: number) => {
            let sunRise = (720.0 - 720.0 / Math.PI * Math.acos(-tanSunPosition) - differenceSunAndLocalTime);

            if (sunRise < 0)
                sunRise += 1440;

            return sunRise;
        }

        const calculateSunSetInternal = (tanSunPosition: number, differenceSunAndLocalTime: number) => {
            let sunSet = (720.0 + 720.0 / Math.PI * Math.acos(-tanSunPosition) - differenceSunAndLocalTime);

            if (sunSet > 1439)
                sunSet -= 1439;

            return sunSet;
        }

        const createDateTime = (timeInMinutes: number) => {
            const hour = Math.ceil(timeInMinutes / 60);
            const minute = timeInMinutes - (hour * 60);

            const now = new Date();
            return new Date(now.getFullYear(), now.getMonth(), now.getDate(), hour, minute);
        }

        const calclateSunRise = (offset: number): Date => {
            const dat = new Date();
            dat.setDate(dat.getDate() + offset)

            const calculateDayOfYear = (date: Date) => Math.floor((date.valueOf() - new Date(date.getFullYear(), 0, 0).valueOf()) / 1000 / 60 / 60 / 24)

            const dayNumberOfDateTime = calculateDayOfYear(dat);
            const differenceSunAndLocalTime = calculateDifferenceSunAndLocalTime(dayNumberOfDateTime);
            const declanationOfTheSun = calculateDeclination(dayNumberOfDateTime);
            const tanSunPosition = calculateTanSunPosition(declanationOfTheSun);
            const sunRiseInMinutes = calculateSunRiseInternal(tanSunPosition, differenceSunAndLocalTime);

            const result = createDateTime(sunRiseInMinutes);

            if (new Date() > result && !recalculationDone) {
                recalculationDone = true;
                return calclateSunRise(1);
            }
            else {
                recalculationDone = false;
                return new Date(dat.getFullYear(), dat.getMonth(), dat.getDate(), result.getHours(), result.getMinutes());
            }
        }

        const calculateSunSet = (offset: number): Date => {
            const dat = new Date();
            dat.setDate(dat.getDate() + offset)

            const calculateDayOfYear = (date: Date) => Math.floor((date.valueOf() - new Date(date.getFullYear(), 0, 0).valueOf()) / 1000 / 60 / 60 / 24)

            const dayNumberOfDateTime = calculateDayOfYear(dat);
            const differenceSunAndLocalTime = calculateDifferenceSunAndLocalTime(dayNumberOfDateTime);
            const declanationOfTheSun = calculateDeclination(dayNumberOfDateTime);
            const tanSunPosition = calculateTanSunPosition(declanationOfTheSun);
            const sunRiseInMinutes = Math.floor(calculateSunSetInternal(tanSunPosition, differenceSunAndLocalTime));

            const result = createDateTime(sunRiseInMinutes);

            if (new Date() > result && !recalculationDone) {
                recalculationDone = true;
                return calculateSunSet(1);
            }
            else {
                recalculationDone = false;
                return new Date(dat.getFullYear(), dat.getMonth(), dat.getDate(), result.getHours(), result.getMinutes());
            }
        }

        return {
            GetSunRiseTime: () => calclateSunRise(0),
            GetSunSetTime: () => calculateSunSet(0)
        }
    }

    protected MapSequence(rawSequenceResponse: any, ignoreRightArgument = false): Sequence {
        //Copy all values that are unchanged between raw and typed Sequence object
        const data = { ...rawSequenceResponse } as Sequence;

        //Create temporary task guid - it allows to remove single task in builder, task guids are not supported by backend so it exists only in library
        //Here we also here handle task backward compatibility issue - remap DeviceTasks to ControllerTasks
        const findLastRightArgumentAndRefactorTasks = (rawSequence: any): void => {
            //Last arg
            if (rawSequence.RightArgumentType == ArgumentTypeNum.Null) {

                //Remap DeviceTasks to ControllerTasks - this code coud be removed in future
                if (!Object.hasOwn(rawSequence.Tasks, "ControllerTasks") && Object.hasOwn(rawSequence.Tasks, "DevicesTasks")) {
                    delete Object.assign(rawSequence.Tasks, { ["ControllerTasks"]: rawSequence.Tasks["DevicesTasks"] })["DevicesTasks"];
                    rawSequence.Tasks.ControllerTasks.map((t: { TaskType: SceneTaskTypeNum; RunParallel: boolean; }) => {
                        t.TaskType = SceneTaskTypeNum.DeviceTask;
                        t.RunParallel = false;
                    })
                }

                //Generate task guids if not exists
                //Task guids are implemented from software 2.9.6.5 so we need to generate them for older SW.
                if (rawSequence.Tasks.ControllerTasks != undefined) {
                    rawSequence.Tasks.ControllerTasks.map((t: { Guid: string | null }) => {
                        if (t?.Guid == null) {
                            t.Guid = Guid.NewGuid();
                        }
                    });
                }
            }
            else {
                if (!Object.hasOwn(rawSequence.Tasks, "ControllerTasks") && Object.hasOwn(rawSequence.Tasks, "DevicesTasks")) {
                    delete Object.assign(rawSequence.Tasks, { ["ControllerTasks"]: rawSequence.Tasks["DevicesTasks"] })["DevicesTasks"];
                }

                return findLastRightArgumentAndRefactorTasks(rawSequence.RightArgument);
            }

        }
        findLastRightArgumentAndRefactorTasks(rawSequenceResponse);

        data._rawRequestData = JSON.parse(JSON.stringify(rawSequenceResponse));

        //Preform mappings for correct types
        if (!ignoreRightArgument) {
            data.RightArgumentType = this.MapArgumentType(rawSequenceResponse.RightArgumentType as ArgumentTypeNum);
            data.RightArgument = this.MapRightArgument(rawSequenceResponse.RightArgument as Sequence, data.RightArgumentType);
        }
        else {
            data.RightArgument = null
            data.RightArgumentType = ArgumentType.Null;
        }
        data.LeftArgumentType = this.MapArgumentType(rawSequenceResponse.LeftArgumentType as ArgumentTypeNum);
        data.LeftArgument = this.MapLeftArgument(rawSequenceResponse.LeftArgument, data.LeftArgumentType);
        data.Tasks.ControllerTasks = this.MapControllerTasks(rawSequenceResponse.Tasks.ControllerTasks);

        if (data.DoesHaveTriggers === undefined) {
            (data as any).DoesHaveTriggers = data.LeftArgumentType == ArgumentType.Null;
        }
        return data;
    }

    /**
     * Functions map all nested right arguments recursively
     * @param arg 
     * @returns 
     */
    protected MapRightArgument(arg: any, argType: ArgumentType): Sequence | null {
        if (arg.HandledType == 0)
            return arg;

        arg.LeftArgumentType = this.MapArgumentType(arg.LeftArgumentType as ArgumentTypeNum);
        arg.LeftArgument = this.MapLeftArgument(arg.LeftArgument, arg.LeftArgumentType);
        arg.Tasks.ControllerTasks = this.MapControllerTasks(arg.Tasks.ControllerTasks);

        //Remove old taks if exists
        if (Object.hasOwn(arg.Tasks, "DevicesTasks"))
            delete (arg.Tasks as any)["DevicesTasks"];

        switch (argType) {
            case ArgumentType.Condition:
                arg.RightArgumentType = this.MapArgumentType(arg.RightArgumentType as ArgumentTypeNum);
                arg.RightArgument = this.MapRightArgument(arg.RightArgument, arg.RightArgumentType);
        }
        return arg;
    }

    /**
     * Function maps raw LeftArgument JSON to object-key typed parameters property
     * @param arg 
     * @returns 
     */
    protected MapLeftArgument(arg: any, argType: ArgumentType): LeftArgument {
        let result = new LeftArgument();
        result.HandledType = arg.HandledType

        switch (argType) {
            case ArgumentType.ArgumentAsAstronomicalClock: {
                result.Argument.ArgumentAsAstronomicalClock = {
                    Position: arg.Argument
                }
                break;
            }

            case ArgumentType.ArgumentAsAstronomicalClockWithOffset: {
                result.Argument.ArgumentAsAstronomicalClockWithOffset = {
                    Offset: arg.Argument.Offset,
                    Position: arg.Argument.Position
                }
                break;
            }

            case ArgumentType.ArgumentAsBoolean: {
                result.Argument.ArgumentAsBoolean = arg.Argument;
                break;
            }

            case ArgumentType.ArgumentAsDayOfWeek: {
                result.Argument.ArgumentAsDayOfWeek = arg.Argument;
                break;
            }

            case ArgumentType.ArgumentAsDaysOfWeek: {
                result.Argument.ArgumentAsDaysOfWeek = arg.Argument;
                break;
            }

            case ArgumentType.ArgumentAsInt: {
                result.Argument.ArgumentAsInt = arg.Argument;
                break;
            }

            case ArgumentType.ArgumentAsTimeout: {
                result.Argument.ArgumentAsTimeout = arg.Argument;
                break;
            }

            case ArgumentType.ArgumentAsTimeSpan: {
                result.Argument.ArgumentAsTimeSpan = {
                    days: arg.Argument.days,
                    hours: arg.Argument.hours,
                    minutes: arg.Argument.minutes,
                    seconds: arg.Argument.seconds
                }
                break;
            }

            case ArgumentType.ArgumentAsTime: {
                result.Argument.ArgumentAsTime = {
                    Hour: arg.Argument.Hour,
                    Minute: arg.Argument.Minute,
                    Second: arg.Argument.Second
                }
                break;
            }

            case ArgumentType.ArgumentAsDeviceState: {
                const devStateArg = new DeviceStateArgument();
                devStateArg.Type = this.MapDeviceStateTypeToResponseType(arg.Argument.DeviceStateType);
                devStateArg.SetCheckDeviceState(this.CreateDeviceState(arg.Argument.CheckedDeviceState, arg.Argument.Channel, devStateArg.Type));
                devStateArg.DeviceGuid = arg.Argument.DeviceGuid;
                devStateArg.ComparisonMethod = arg.Argument.ComparisonMethod == undefined ? DeviceStateComparisonMethod.Default : arg.Argument.ComparisonMethod;

                if (arg.Argument.ComparisonParams != null) {
                    Object.entries(arg.Argument.ComparisonParams).forEach(k => {
                        switch (k[0]) {
                            case "DeltaValue":
                            case "StateRepeatCount":
                                devStateArg.SetComparisonParam(DeviceStateComparisonParams[k[0]], new NumberParam(k[1] as number));
                                break;
                            case "DeltaCalculationTimeRange":
                            case "StateRepeatTimeRange":
                                devStateArg.SetComparisonParam(DeviceStateComparisonParams[k[0]], new TimeSpanParam(k[1] as string));
                                break;
                        }

                    })
                }

                result.Argument.ArgumentAsDeviceState = devStateArg
                break;
            }
            case ArgumentType.ArgumentAsConditionInfo: {
                result.Argument.ArgumentAsConditionInfo = {
                    DeviceGuid: arg.Argument.DeviceGuid
                }
                break;
            }
        }

        return result;
    }

    /**
     * Function changes numeric based enum to string based enum, string enum is more user frienldy and is used for key of left argument typed property
     * @param argumentType
     * @returns 
     */
    protected MapArgumentType(argumentType: ArgumentTypeNum): ArgumentType {
        return ArgumentType[ArgumentTypeNum[argumentType] as keyof typeof ArgumentType]
    }

    /**
     * Maps types of device state from Scenes extension state enum to general device states enum in library
     * @param stateType 
     * @returns 
     */
    protected MapDeviceStateTypeToResponseType(stateType: number): DeviceResponseType {
        switch (stateType as DeviceStateType) {
            case DeviceStateType.BatteryState:
                return DeviceResponseType.BatteryState;
            case DeviceStateType.BinarySensor:
                return DeviceResponseType.BinarySensorState;
            case DeviceStateType.BlindPosition:
                return DeviceResponseType.BlindPosition;
            case DeviceStateType.Brightness:
                return DeviceResponseType.MeasuredBrightness;
            case DeviceStateType.ButtonState:
                return DeviceResponseType.RemoteButtonState;
            case DeviceStateType.ChannelOnOff:
                return DeviceResponseType.ChannelOnOffState;
            case DeviceStateType.DoorBell:
                return DeviceResponseType.DoorBellState;
            case DeviceStateType.Energy:
                return DeviceResponseType.MeasuredEnergy;
            case DeviceStateType.FloodSensor:
                return DeviceResponseType.FloodSensorState;
            case DeviceStateType.GatePosition:
                return DeviceResponseType.GatePosition;
            //case DeviceStateType.HeatSensor:
            //  return DeviceResponseType.
            case DeviceStateType.LightBrightness:
                return DeviceResponseType.LightBrightness;
            case DeviceStateType.LightColor:
                return DeviceResponseType.LightColor;
            case DeviceStateType.LightTemperature:
                return DeviceResponseType.LightWarmth;
            //case DeviceStateType.MeasuredCurrent:
            //    return DeviceResponseType.
            //case DeviceStateType.MeasuredPower:
            //    return DeviceResponseType
            // case DeviceStateType.MeasuredVoltage:
            //    return DeviceResponseType.
            case DeviceStateType.ReedState:
                return DeviceResponseType.ReedState;
            case DeviceStateType.SmokeSensor:
                return DeviceResponseType.SmokeSensorState;
            case DeviceStateType.Temperature:
                return DeviceResponseType.MeasuredTemperature;
            case DeviceStateType.Humidity:
                return DeviceResponseType.HumiditySensorState;
            case DeviceStateType.BlindsControlButton:
                return DeviceResponseType.BlindRemoteButtonState;
            case DeviceStateType.FacadeControlButton:
                return DeviceResponseType.FacadeRemoteButtonState;
            case DeviceStateType.Movement:
                return DeviceResponseType.MovementSensorState;
            case DeviceStateType.AirPressure:
                return DeviceResponseType.PressureSensorState;
            case DeviceStateType.WindSpeed:
                return DeviceResponseType.WindSpeedState;
            case DeviceStateType.WindThreshold:
                return DeviceResponseType.CurrentWindThreshold
            default:
                return DeviceResponseType.Unknown;
        }
    }

    /**
     * Function converts device state from Scenes extension to general device states in library
     * @param argumentData 
     * @param responseType 
     * @returns 
     */
    protected CreateDeviceState<T extends IDeviceStateData>(argumentData: any, channel: number, responseType: DeviceResponseType): T {
        switch (responseType) {
            case DeviceResponseType.BatteryState: {
                const state: BatteryState = {
                    Channel: channel,
                    StateReliability: StateReliability.Unconfident,
                    Percentage: argumentData.Percentage,
                    State: argumentData.State,
                    Time: "",
                };

                return state as unknown as T;
            }

            case DeviceResponseType.BinarySensorState: {
                const state: BinarySensorState = {
                    Channel: channel,
                    StateReliability: StateReliability.Unconfident,
                    State: argumentData.State,
                    Time: "",
                };

                return state as unknown as T;
            }

            case DeviceResponseType.BlindPosition: {
                const state: BlindPostionState = {
                    Channel: channel,
                    StateReliability: StateReliability.Unconfident,
                    Position: argumentData.Position,
                    RawPosition: argumentData.RawPosition,
                    Time: "",
                };

                return state as unknown as T;
            }

            case DeviceResponseType.LightBrightness: {
                const state: LightBrightnessState = {
                    Channel: channel,
                    StateReliability: StateReliability.Unconfident,
                    Brightness: argumentData.Brightness,
                    Time: "",
                };

                return state as unknown as T;
            }

            case DeviceResponseType.MeasuredBrightness: {
                const state: MeasuredBrightnessState = {
                    Channel: channel,
                    StateReliability: StateReliability.Unconfident,
                    Brightness: argumentData.Brightness,
                    Time: "",
                };

                return state as unknown as T;
            }

            case DeviceResponseType.RemoteButtonState: {
                const state: RemoteButtonState = {
                    Channel: channel,
                    StateReliability: StateReliability.Unconfident,
                    State: argumentData.State,
                    Time: "",
                };

                return state as unknown as T;
            }

            case DeviceResponseType.ChannelOnOffState: {
                const state: ChannelOnOffState = {
                    Channel: channel,
                    StateReliability: StateReliability.Unconfident,
                    State: argumentData.State,
                    Time: "",
                };

                return state as unknown as T;
            }

            case DeviceResponseType.DoorBellState: {
                const state: DoorBellState = {
                    Channel: channel,
                    StateReliability: StateReliability.Unconfident,
                    State: argumentData.State,
                    Time: "",
                };

                return state as unknown as T;
            }

            case DeviceResponseType.MeasuredEnergy: {
                const params = new Map<EnergyMeasurementParameter, number>()

                for (let [k, v] of Object.entries(argumentData.MeasurementParameters))
                    params.set(EnergyMeasurementParameter[k as keyof typeof EnergyMeasurementParameter], v as number)

                const state: MeasuredEnergyState = {
                    Channel: channel,
                    StateReliability: StateReliability.Unconfident,
                    MeasurementParameters: params,
                    Time: "",
                };

                return state as unknown as T;
            }

            case DeviceResponseType.FloodSensorState: {
                const state: FloodSensorState = {
                    Channel: channel,
                    StateReliability: StateReliability.Unconfident,
                    State: argumentData.State,
                    Time: "",
                };

                return state as unknown as T;
            }

            case DeviceResponseType.GatePosition: {
                const state: GatePositionState = {
                    Channel: channel,
                    StateReliability: StateReliability.Unconfident,
                    Position: argumentData.Position,
                    PositionType: argumentData.PositionType,
                    Time: "",
                };

                return state as unknown as T;
            }

            case DeviceResponseType.LightColor: {
                const state: LightColorState = {
                    Channel: channel,
                    StateReliability: StateReliability.Unconfident,
                    R: argumentData.R,
                    G: argumentData.G,
                    B: argumentData.B,
                    Time: "",
                };

                return state as unknown as T;
            }

            case DeviceResponseType.LightWarmth: {
                const state: LightWarmthState = {
                    Channel: channel,
                    StateReliability: StateReliability.Unconfident,
                    Temperature: argumentData.Temperature,
                    Time: "",
                };

                return state as unknown as T;
            }

            case DeviceResponseType.ReedState: {
                const state: ReedState = {
                    Channel: channel,
                    StateReliability: StateReliability.Unconfident,
                    State: argumentData.State,
                    Time: "",
                };

                return state as unknown as T;
            }

            case DeviceResponseType.SmokeSensorState:
                throw new UnsupportedScenesDeviceState("Unsupported device state!");


            case DeviceResponseType.MeasuredTemperature: {
                const state: MeasuredTemperatureState = {
                    Channel: channel,
                    StateReliability: StateReliability.Unconfident,
                    Temperature: argumentData.Temperature,
                    Time: "",
                    Type: argumentData.Type
                };

                return state as unknown as T;
            }

            case DeviceResponseType.HumiditySensorState: {
                const state: HumiditySensorState = {
                    Channel: channel,
                    StateReliability: StateReliability.Unconfident,
                    Humidity: argumentData.Humidity,
                    Time: "",
                    Type: argumentData.Type
                };

                return state as unknown as T;
            }

            case DeviceResponseType.BlindRemoteButtonState: {
                const state: BlindRemoteButtonState = {
                    Channel: channel,
                    StateReliability: StateReliability.Unconfident,
                    State: argumentData.State,
                    Time: "",
                };

                return state as unknown as T;
            }

            case DeviceResponseType.LightRGBWState: {
                const state: LightRGBWState = {
                    StateReliability: StateReliability.Unconfident,
                    Channel: channel,
                    Time: "",
                    Brightness: 100,
                    R: argumentData.R,
                    G: argumentData.G,
                    B: argumentData.B,
                    W: argumentData.W,
                };

                return state as unknown as T;
            }

            case DeviceResponseType.FacadeRemoteButtonState: {
                const state: FacadeRemoteButtonState = {
                    Channel: channel,
                    Time: "",
                    StateReliability: StateReliability.Unconfident,
                    State: argumentData.State
                };

                return state as unknown as T;
            }

            case DeviceResponseType.MovementSensorState: {
                const state: MovementSensorState = {
                    Channel: channel,
                    Time: "",
                    StateReliability: StateReliability.Unconfident,
                    Movement: argumentData.Movement,
                    Intensity: argumentData.Intensity
                };

                return state as unknown as T;
            }

            case DeviceResponseType.PressureSensorState: {
                const state: PressureSensorState = {
                    Channel: channel,
                    Time: "",
                    StateReliability: StateReliability.Unconfident,
                    Pressure: argumentData.Pressure,
                    PressureType: argumentData.PressureType
                };

                return state as unknown as T;
            }

            case DeviceResponseType.WindSpeedState: {
                const state: WindSpeedState = {
                    Channel: channel,
                    Time: "",
                    StateReliability: StateReliability.Unconfident,
                    Value: argumentData.Value
                };

                return state as unknown as T;
            }

            case DeviceResponseType.CurrentWindThreshold: {
                const state: CurrentWindThresholdState = {
                    Channel: channel,
                    Time: "",
                    StateReliability: StateReliability.Unconfident,
                    WindThreshold: argumentData.WindThreshold
                };

                return state as unknown as T;
            }

            case DeviceResponseType.Unknown:
                return new DeviceState<any>() as unknown as T;
            default:
                throw new UnsupportedScenesDeviceState("Unsupported device state!");
        }
    }

    protected MapDeviceTask(rawTasks: any): IDeviceTask {
        let result: DeviceTask = new DeviceTask();

        let tsk = rawTasks as RawDeviceTask;
        DependencyContainer.Log?.Debug(`[TASK] object: ${JSON.stringify(tsk)}`);

        switch (tsk.ControlFeature) {
            case DeviceControlFeature.TurnOn: {
                result = new TurnOn();
                result.TaskType = DeviceTaskType.TurnOn;
                break;
            }
            case DeviceControlFeature.TurnOff: {
                result = new TurnOff();
                result.TaskType = DeviceTaskType.TurnOff;
                break;
            }
            case DeviceControlFeature.ToggleState: {
                result = new ToggleState();
                result.TaskType = DeviceTaskType.TogleState;
                break;
            }

            case DeviceControlFeature.SetBlindPosition: {
                const dev = Api.Get<IDevicesService>(DevicesService.ServiceName).GetDevice(tsk.DeviceGuid);
                if (dev?.AvailableTaskTypes.any(a => a.InterfaceType == "IBlindPositionSimple")) {
                    result = new SetBlindPositionSimple();
                    result.TaskType = DeviceTaskType.SetBlindPositionSimple;
                    (result as SetBlindPositionSimple).Action = tsk.Data.Action;
                }
                else {
                    result = new SetBlindPosition();
                    result.TaskType = DeviceTaskType.SetBlindPosition;
                    (result as SetBlindPosition).Action = tsk.Data.Action;
                    (result as SetBlindPosition).Position = tsk.Data.Position;
                }
                break;
            }

            case DeviceControlFeature.SetLightColor:
                result = new SetLightColor();
                result.TaskType = DeviceTaskType.SetLightColor;
                (result as SetLightColor).R = tsk.Data.R;
                (result as SetLightColor).G = tsk.Data.G;
                (result as SetLightColor).B = tsk.Data.B;
                break;

            case DeviceControlFeature.SetLightTemperature:
                result = new SetLightTemperature();
                result.TaskType = DeviceTaskType.SetLightTemperature;
                (result as SetLightTemperature).Temperature = tsk.Data.Temperature;
                break;

            case DeviceControlFeature.SetLightBrightness:
                result = new SetLightBrightness();
                result.TaskType = DeviceTaskType.SetLightBrightness;
                (result as SetLightBrightness).Brightness = tsk.Data.Brightness;
                break;

            case DeviceControlFeature.SetTemperature:
                result = new SetTemperature();
                result.TaskType = DeviceTaskType.SetTemperature;
                (result as SetTemperature).Temperature = tsk.Data.Temperature;
                break;

            case DeviceControlFeature.GetState:
                result = new GetDeviceState();
                result.TaskType = DeviceTaskType.GetChannelsState;
                break;

            case DeviceControlFeature.TurnOnWithTimeout:
                result = new TurnOnWithTime();
                result.TaskType = DeviceTaskType.TurnOnWithTime;
                (result as TurnOnWithTime).SwitchOffDelaySeconds = tsk.Data.SwitchOffDelaySeconds;
                break;

            case DeviceControlFeature.SetLightBrightnessDynamicly:
                result = new SetLightBrightnessDynamicly();
                result.TaskType = DeviceTaskType.SetLightBrightnessDynamicly;
                (result as SetLightBrightnessDynamicly).Brightness = tsk.Data.Brightness;
                break;

            case DeviceControlFeature.IdentifyDevice:
                result = new IdentifyDevice();
                result.TaskType = DeviceTaskType.IdentifyDevice;
                break;

            case DeviceControlFeature.SetBlindOpenCloseTime:
                result = new SetBlindOpenCloseTime();
                result.TaskType = DeviceTaskType.SetBlindOpenCloseTime;
                (result as SetBlindOpenCloseTime).Data.CloseTime = tsk.Data;
                break;

            case DeviceControlFeature.SetBlindMicroventilation:
                result = new SetBlindMicroventilation();
                result.TaskType = DeviceTaskType.SetBlindMicroventilation;
                (result as SetBlindMicroventilation).Position = tsk.Data.Position;
                break;

            case DeviceControlFeature.GatewayPulse:
                result = new ChangeGatewayPositionPulse();
                result.TaskType = DeviceTaskType.ChangeGatewayPositionPulse;
                break;

            case DeviceControlFeature.GatePulse:
                result = new ChangeGatePositionPulse();
                result.TaskType = DeviceTaskType.ChangeGatePositionPulse;
                break;

            case DeviceControlFeature.SetFacadePositionAndTilt:
                result = new SetFacadePosition();
                result.TaskType = DeviceTaskType.SetFacadePosition;
                (result as SetFacadePosition).Position = tsk.Data.Position;
                (result as SetFacadePosition).Tilt = tsk.Data.Tilt;
                (result as SetFacadePosition).FacadeAction = FacadeActionEnum.TiltAndPercentage;
                break;

            case DeviceControlFeature.GetFacadeType:
                result = new GetFacadeType();
                result.TaskType = DeviceTaskType.GetFacadeType;
                break;

            case DeviceControlFeature.LightRGBW:
                result = new LightRGBW();
                result.TaskType = DeviceTaskType.LightRGBW;
                (result as LightRGBW).Data = tsk.Data;
                break;

            case DeviceControlFeature.SetGatePositionPrecise:
                result = new SetGatePositionPrecise();
                result.TaskType = DeviceTaskType.PreciseGateControl;
                (result as SetGatePositionPrecise).Position = tsk.Data.Position;
                (result as SetGatePositionPrecise).GateControlAction = tsk.Data.GateControlAction;
                break;

            default:
                break;

        }

        result.Channel = tsk.Channel;
        result.DeviceGuid = tsk.DeviceGuid;
        result.FeatureType = tsk.ControlFeature;
        result.Guid = Guid.NewGuid();

        return result;
    }

    protected MapControllerTasks(rawTasks: any[]): ControllerTasks[] {
        const result = [];
        DependencyContainer.Log?.Debug(`[TASKS] received tasks object: ${JSON.stringify(rawTasks)}`);
        for (let tsk of rawTasks as RawSceneTask[]) {
            const task = new ControllerTasks();
            task.Guid = tsk.Guid;
            task.TaskId = Helpers.GenerateMd5(JSON.stringify(tsk))

            switch (tsk.TaskType) {
                case SceneTaskTypeNum.DeviceTask:
                    task.TaskType = SceneTaskType.DeviceTask;
                    task.Task.DeviceTask = this.MapDeviceTask(tsk)
                    break;
                case SceneTaskTypeNum.DelayTask:
                    task.TaskType = SceneTaskType.DelayTask;
                    task.Task.DelayTask = {
                        Delay: new TimeSpanParam((tsk as any).Delay)
                    }
                    break;
                case SceneTaskTypeNum.NotifyTask:
                    task.TaskType = SceneTaskType.NotifyTask;
                    task.Task.NotifyTask = {
                        Message: (tsk as any).Message,
                        NotifyType: (tsk as any).NotifyType,
                        NotificationClients: (tsk as any).NotificationClients
                    }
                    break;
                case SceneTaskTypeNum.LockExecution:
                case SceneTaskTypeNum.LockExecution:
                    task.TaskType = SceneTaskType.LockExecution;
                    DependencyContainer.Log?.Debug(`[TASKS] received task LockExecution object: ${JSON.stringify(tsk)}`);
                    task.Task.LockExecution = {
                        LockStartHour: new TimeSpanParam((tsk as any).LockStartHour as string),// (tsk as any).LockStartHour as TimeSpanParam, //
                        LockEndHour: new TimeSpanParam((tsk as any).LockEndHour as string),// (tsk as any).LockEndHour as TimeSpanParam, //
                        LockType: (tsk as any).LockType,
                        ResetLockScenesGuids: (tsk as any).ResetLockScenesGuids
                    }
                    break;
                case SceneTaskTypeNum.Unknown:
                    break;
            }
            result.push(task);
        }

        return result;
    }

    public async GetSupportedTaskTypesAsync(): Promise<SupportedTaskTypes[]> {
        const version = await Api.Get<IUpdatesProvider>(UpdatesProvider.ServiceName).GetSoftwareRuntimeInfoAsync();
        const [major, minor] = version.SoftwareVersion.split(".").map(v => parseInt(v));

        const result = [SupportedTaskTypes.DeviceTask];

        if (major > 5 || (major == 5 && minor >= 27))
            result.push(SupportedTaskTypes.DelayTask);

        if (major > 5 || (major == 5 && minor >= 37))
            result.push(SupportedTaskTypes.LockExecutionTask);

        if (major > 6 || (major == 6 && minor >= 3))
            result.push(SupportedTaskTypes.NotifyTaskAppNotify);

        return result;
    }

}

//Sequences list
class GetSequencesListRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/logic/sequences/list";
        this.Method = Method.Get;
    }
}

//Sequence details
class GetSequenceDetailsRequest extends DataFrame<any> {
    constructor(sequenceGuid: string) {
        super();
        this.Resource = "/logic/sequence";
        this.Method = Method.Get;
        this.Data = sequenceGuid;
    }
}

//Sequence used in
class GetSequenceUsedInRequest extends DataFrame<any> {
    constructor(sequenceGuid: string) {
        super();
        this.Resource = "/logic/sequences/used";
        this.Method = Method.Get;
        this.Data = sequenceGuid;
    }
}

class Sequence implements ISequence, IRawSequence {
    _rawRequestData: SequenceRequestData = new SequenceRequestData();
    Tasks: ISequenceTasks = { ControllerTasks: [] };
    Guid: string = "";
    ObjectType: number = 1;
    Name: string = "";
    Icon: string = "";
    AtMeetCondition: boolean = false;
    ExecuteOnce: boolean = false;
    IsDisabled: boolean = false;
    HandledType: HandledType = HandledType.Unknown
    ConditionType: ConditionsTypes = ConditionsTypes.Unknown;
    ConditionTimeout: number = 0;
    LeftArgumentType: ArgumentType = ArgumentType.Null;
    RightArgumentType: ArgumentType = ArgumentType.Null;
    LeftArgument: ILeftArgument = new LeftArgument();
    RightArgument: ISequence | null = null;

    get DoesHaveTriggers(): boolean {
        return this.LeftArgumentType == ArgumentType.Null;
    }
}

class LeftArgument implements ILeftArgument {
    HandledType: ArgumentTypeNum = ArgumentTypeNum.Null;
    Argument: LeftArgumentTypes = {} as LeftArgumentTypes;
}

enum ArgumentTypeNum {
    Null = 0,
    Condition = 1,
    ConditionGroup = 2,
    ArgumentAsInt = 3,
    ArgumentAsTimeSpan = 4,
    ArgumentAsBoolean = 5,
    ArgumentAsTimeout = 6,
    ArgumentAsDayOfWeek = 7,
    ArgumentAsDaysOfWeek = 8,
    ArgumentAsAstronomicalClock = 9,
    ArgumentAsTime = 10,
    ArgumentAsDeviceState = 11,
    ArgumentAsNumber = 12,
    ArgumentAsAstronomicalClockWithOffset = 13,
    ArgumentAsConditionInfo = 14,
}

type RawSceneTask = {
    TaskType: SceneTaskTypeNum
    RunParallel: boolean;
    Guid: string;
    _taskId: string;
}

type RawDeviceTask = {
    DeviceGuid: string;
    Channel: number;
    ControlFeature: DeviceControlFeature;
    Data: any;
}

class ControllerTasks implements ISceneTask {
    TaskId: string = "";
    Guid: string = "";
    TaskType: SceneTaskType = SceneTaskType.Unknown;
    Task: SceneTasks = {} as SceneTasks;
}

//Create sequence
class CreateSequenceRequest extends DataFrame<SequenceRequestData> {
    constructor(data: SequenceRequestData) {
        super();
        this.Resource = "/logic/sequence";
        this.Method = Method.Put;
        this.Data = data;
    }
}

//Edit sequence
class EditSequenceRequest extends DataFrame<EditSequenceRequestFrame> {
    constructor(data: EditSequenceRequestFrame) {
        super();
        this.Resource = "/logic/sequence/edit";
        this.Method = Method.Put;
        this.Data = data;
    }
}

class EditSequenceRequestFrame {
    Guid: string = "";
    Icon: string = "";
    Name: string = "";
    Condition: SequenceRequestData = new SequenceRequestData();
}

//Delete sequence
class DeleteSequenceRequest extends DataFrame<string> {
    constructor(sequenceGuid: string) {
        super();
        this.Resource = "/logic/sequence";
        this.Method = Method.Delete;
        this.Data = sequenceGuid;
    }
}

//Enable sequence
class EnableSequenceRequest extends DataFrame<string> {
    constructor(sequenceGuid: string) {
        super();
        this.Resource = "/logic/sequence/enable";
        this.Method = Method.Post;
        this.Data = sequenceGuid;
    }
}

//Disable sequence
class DisableSequenceRequest extends DataFrame<string> {
    constructor(sequenceGuid: string) {
        super();
        this.Resource = "/logic/sequence/disable";
        this.Method = Method.Post;
        this.Data = sequenceGuid;
    }
}
